import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { useFileSystemDuplexer } from './use-file-system-duplexer';
import { queryKeys } from './query-client';
import { builtByOf } from './file-record';

// Client data layer for the workspace "Files & Apps" surfaces (the "Agents at work" strip, the gallery
// page, and the file-detail page). One fetch to /api/workspace-files?workspaceId= returns the workspace's
// artifacts — its agents' apps and files, aggregated across the workspace + its child projects and
// flattened (apps first, then files, each alphabetical) by the route. orion-web's CLIENT bundle must not
// import common-lib, so the item shape lives here as a client-local contract matching the route.

/** One artifact for the file cards: an app (name only) or a file (name + inline content + extension). */
export interface WorkspaceFile {
  name: string;
  isApp: boolean;
  extension?: string;
  /** For files, the artifact body (inline content or an https URL). Apps carry none. */
  content?: string;
  /** Which agent produced this artifact, for the gallery's "Built by" column and the agent tag on every
   *  file card. Derived CLIENT-SIDE from the file's FileRecord header (see `fetchWorkspaceFiles`) —
   *  `GetWorkspaceArtifacts` has no attribution field of its own, so the header is the source.
   *
   *  Undefined for every file today: nothing writes those header fields yet (the write side is a separate
   *  harness change — see `lib/file-record.ts`), so the column renders and reads "—". This read path is
   *  what makes it fill in with no further web work. */
  builtBy?: { agentId: string; name: string };
  /** Server time the file was last written (ISO string). Absent for files written before the backend
   *  started recording it — treat as "unknown", sort after every dated file rather than as oldest. */
  updatedDate?: string;
  /** When the file was first written (ISO string), same caveat as `updatedDate`. The date filter falls back
   *  to it so a file written once and never touched again is still datable. */
  createdDate?: string;
  /** The file's one-line descriptor, when the writer left one. */
  hint?: string;
  /** A one-line description of the creation, shown under the name on the mobile gallery card. Mirrors
   *  native's gated `WorkspaceArtifactItem.subtitle` (iOS/Android `AgentCreationsScreen.cardSubtitle`):
   *  the BFF carries no source for it yet, so it is undefined for every file today and the card omits the
   *  line rather than fabricating one. The card fills in with no further web work once the write side lands. */
  subtitle?: string;
  /** The stored type, when the writer declared one. Beats inferring from the extension. */
  contentType?: string;
  /** The writer's own byte count — for a reference, the real upload size rather than the length of
   *  whatever string the artifact is stored as. */
  sizeBytes?: string;
  /** Set when the bytes live elsewhere; `content` is then empty by design. Read it through
   *  `effectiveContent`, never on its own — that is what keeps the two body shapes one code path. */
  referenceUrl?: string;
  /** The writer's actor kind, which is what decides the Uploaded/Agent bucket for a file whose body
   *  carries no header saying so. */
  writerType?: string;
  /** Which Space the file belongs to. Sent only on the ACCOUNT-WIDE read (`fetchAllWorkspaceFiles`) —
   *  a scoped read would repeat the caller's own id on every row, so the route omits it there. */
  workspaceId?: string;
}

export type WorkspaceFilesState =
  | { status: 'loading' }
  | { status: 'ready'; items: WorkspaceFile[] }
  | { status: 'error' };

export async function fetchWorkspaceFiles(workspaceId?: string): Promise<WorkspaceFile[]> {
  const query = workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : '';
  const res = await apiFetch(`${API_BASE}/api/workspace-files${query}`, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const data = (await res.json()) as { items?: WorkspaceFile[] };
  // Attribution is resolved here, once, so every consumer of a `WorkspaceFile` (the gallery table, the
  // "Agents at work" strip, the mobile card, the detail panel — all of which already read `builtBy`) tells
  // the same story.
  //
  // The HEADER wins over the route's writer-derived value. Only a sub-agent running AS a named agent has an
  // agent ENTITY, and its id is what gives the dot a stable hue; the route can only offer the actor KIND
  // (`writer.type`) as a stand-in. It used to be the other way round (`item.builtBy ?? builtByOf(...)`),
  // which meant a file that named its real agent still displayed as its actor kind. The route's value
  // remains the fallback, and a `builtBy` synthesized by the dev Mock-data layer still works.
  return (data.items ?? []).map((item) => ({ ...item, builtBy: builtByOf(item.content) ?? item.builtBy }));
}

export function useWorkspaceFiles(workspaceId: string | undefined): WorkspaceFilesState {
  const query = useQuery({
    queryKey: queryKeys.workspaceFiles(workspaceId),
    queryFn: () => fetchWorkspaceFiles(workspaceId as string),
    enabled: !!workspaceId,
  });

  // The agents' world changing (a new file/app produced) refreshes the list live.
  useFileSystemDuplexer(() => {
    if (workspaceId) void query.refetch();
  });

  // Keep a prior good list on a transient refetch failure — only surface an error before the first success.
  if (query.data) return { status: 'ready', items: query.data };
  if (query.status === 'error') return { status: 'error' };
  return { status: 'loading' };
}

/**
 * EVERY Space's files, in one request — each item carrying the `workspaceId` it came from.
 *
 * The route already reads the whole file-system map and then discards everything outside one Space's
 * prefix, so this costs the same single read a scoped fetch does. It exists because the surfaces that
 * want a cross-workspace answer (the `/files` browser, and the Files tile's stat line on Your Agents
 * and on a Space's Dashboard) were otherwise obliged to fan out one request PER Space to assemble it.
 *
 * `enabled` is here for the callers that mount the tile in a presentation with no stat line to fill —
 * they must not pay for a read they will not render.
 */
export function useAllWorkspaceFiles(options?: { enabled?: boolean }): WorkspaceFilesState {
  const enabled = options?.enabled ?? true;
  const query = useQuery({
    queryKey: queryKeys.allWorkspaceFiles,
    queryFn: () => fetchWorkspaceFiles(),
    enabled,
  });

  useFileSystemDuplexer(() => {
    if (enabled) void query.refetch();
  });

  if (query.data) return { status: 'ready', items: query.data };
  if (query.status === 'error') return { status: 'error' };
  return { status: 'loading' };
}
